export const SAVE_KEY_STORAGE = 'tavern-netplay-save-library-v1';
export const SAVE_REQUEST_MS = 8000;
export const SAVE_OPERATION_MS = 20000;

// Shared across tabs; unlike a controller token this key survives tab/service restarts.
export function persistentSaveKey(sessionToken, storage = window.localStorage, random = window.crypto) {
  try {
    let key = storage.getItem(SAVE_KEY_STORAGE);
    if (!/^[a-f0-9]{64}$/.test(key || '') || key === sessionToken) {
      key = Array.from(random.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');
      if (key === sessionToken) throw new Error('Key collision');
      storage.setItem(SAVE_KEY_STORAGE, key);
    }
    if (storage.getItem(SAVE_KEY_STORAGE) !== key) throw new Error('Storage unavailable');
    return key;
  } catch {
    throw new Error('Allow persistent browser storage to use named saves. Session snapshots are not a durable save library.');
  }
}

export function saveName(value, limit = 64) {
  const name = String(value || '').trim();
  if (!name || Array.from(name).length > limit || /[\x00-\x1f\x7f]/.test(name)) throw new Error('Choose a save name of 1–' + limit + ' characters without control characters.');
  return name;
}

export function namedSaveFilename(slot, basePath) {
  const name = String(slot.name || 'save').replace(/[\\/:*?"<>|\x00-\x1f\x7f]/g, '-').slice(0, 64).replace(/^[. ]+|[. ]+$/g, '') || 'save';
  return name + '-game-' + (basePath.replace(/\/+$/, '').endsWith('/netplay') ? '1' : '2') + '-' + slot.id.slice(0, 8) + '.state';
}

export function namedSaveApi({ basePath, sessionToken, saveKey, fetcher = fetch }) {
  const headers = { Authorization: 'Bearer ' + sessionToken, 'X-Netplay-Save-Key': saveKey };
  async function request(path, download, signal) {
    const controller = new AbortController();
    let timer, abort;
    const deadline = new Promise((_, reject) => {
      abort = () => { controller.abort(); reject(new Error('Save request cancelled.')); };
      timer = setTimeout(() => { controller.abort(); reject(new Error('The save library request timed out. Try Refresh saves.')); }, SAVE_REQUEST_MS);
      if (signal?.aborted) abort(); else signal?.addEventListener('abort', abort, { once: true });
    });
    const task = (async () => {
      const response = await fetcher(basePath + path, { headers, cache: 'no-store', signal: controller.signal });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(String(result.error || 'The save library is unavailable. Try again.').slice(0, 300));
      }
      return download ? response.blob() : response.json();
    })();
    try { return await Promise.race([task, deadline]); }
    finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
  }
  return { list: signal => request('/saves', false, signal), download: (id, signal) => request('/saves/' + encodeURIComponent(id), true, signal) };
}
